import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import Typography from '@/components/ui/typography';
import { CategoryContext } from '@/lib/CategoryContext';
import { siteStats, spellOutNumber } from '@/lib/siteStats';
import { Link, usePage } from '@inertiajs/react';
import axios, { AxiosError } from 'axios';
import { useContext, useState, FormEvent } from 'react';
import { toast } from 'sonner';

import {
  BrandIconProps,
  Facebook,
  Instagram,
  Linkedin,
  Twitter,
  Youtube,
} from './icons/BrandIcons';
import { Routes } from './Routes';

/*
 * These were five PNG files at 32px. Icons keep the row optically consistent,
 * scale cleanly, and inherit the footer's colour.
 */
const socialMediaPlatforms: {
  name: string;
  icon: React.ForwardRefExoticComponent<BrandIconProps>;
  url: string;
}[] = [
  {
    name: 'Facebook',
    icon: Facebook,
    url: 'https://www.facebook.com/priya.graj.5',
  },
  {
    name: 'Instagram',
    icon: Instagram,
    url: 'https://www.instagram.com/samuthrika_academy_/',
  },
  {
    name: 'YouTube',
    icon: Youtube,
    url: 'https://www.youtube.com/@user-gw3uf7qo7j',
  },
  { name: 'X', icon: Twitter, url: 'https://twitter.com/SamuthrikaPriya' },
  {
    name: 'LinkedIn',
    icon: Linkedin,
    url: 'https://www.linkedin.com/in/samuthrika-academy-425a4a2a4',
  },
];

const legalLinks = [
  { title: 'Privacy policy', path: 'privacy-policy' },
  { title: 'Disclaimer', path: 'disclaimer' },
];

const Footer = () => {
  const { url } = usePage();
  const { branches } = useContext(CategoryContext);
  const [email, setEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);

  const handleSubscribe = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubscribing(true);

    try {
      const response = await axios.post('/api/newsletter-subscribe', { email });
      toast.success((response.data as { message: string }).message);
      window.dataLayer?.push({ event: 'newsletter_signup' });
      setEmail('');
    } catch (error) {
      toast.error(
        (error as AxiosError<{ errors: { email: string[] } }>).response?.data
          .errors.email[0] ?? 'Something went wrong. Try again.'
      );
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <footer className="w-full bg-ink text-ink-foreground">
      <div className="mx-auto max-w-7xl px-5 py-14 md:px-8 md:py-20 lg:px-12">
        <div className="grid gap-12 lg:grid-cols-[1.2fr_1fr_1fr]">
          <div className="flex flex-col gap-5">
            <Typography.Display3 className="text-2xl md:text-3xl lg:text-3xl">
              Samuthrika Academy
            </Typography.Display3>
            <Typography.Subtle className="max-w-sm text-ink-foreground/70">
              Teaching fashion, tailoring, aari, beauty and more across{' '}
              {spellOutNumber(siteStats.branches)} branches in Tamil Nadu since
              2011.
            </Typography.Subtle>

            <form
              className="flex w-full max-w-sm flex-col gap-2"
              onSubmit={(event) => void handleSubscribe(event)}
            >
              <label
                htmlFor="newsletter-email"
                className="text-sm font-medium text-ink-foreground/70"
              >
                Get course updates by email
              </label>
              <div className="flex items-center gap-2">
                <Input
                  id="newsletter-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  className="border-ink-foreground/20 bg-ink-foreground/5 text-ink-foreground placeholder:text-ink-foreground/40"
                />
                <Button type="submit" disabled={subscribing}>
                  {subscribing ? 'Subscribing' : 'Subscribe'}
                </Button>
              </div>
            </form>

            <div className="mt-2 flex flex-row items-center gap-3">
              {socialMediaPlatforms.map((platform) => (
                <a
                  key={platform.name}
                  href={platform.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={platform.name}
                  className="inline-flex size-9 items-center justify-center rounded-full border border-ink-foreground/20 text-ink-foreground/70 transition-colors hover:border-primary hover:text-primary"
                >
                  <platform.icon size={17} strokeWidth={1.75} />
                </a>
              ))}
            </div>
          </div>

          <nav aria-label="Site" className="flex flex-col gap-3">
            <Typography.Small className="font-semibold uppercase tracking-[0.18em] text-ink-foreground/50">
              Explore
            </Typography.Small>
            <ul className="flex flex-col gap-2">
              {Routes.map((link) => (
                <li key={link.label}>
                  <Link
                    href={route(link.path)}
                    className={
                      url.includes(`/${link.path}`)
                        ? 'text-sm text-primary'
                        : 'text-sm text-ink-foreground/70 transition-colors hover:text-primary'
                    }
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
              <li>
                <a
                  href="/blog"
                  className="text-sm text-ink-foreground/70 transition-colors hover:text-primary"
                >
                  Blogs
                </a>
              </li>
            </ul>
          </nav>

          <nav aria-label="Branches" className="flex flex-col gap-3">
            <Typography.Small className="font-semibold uppercase tracking-[0.18em] text-ink-foreground/50">
              Branches
            </Typography.Small>
            <ul className="flex flex-col gap-2">
              {branches.map((branch) => (
                <li key={branch.name}>
                  <Link
                    href={`/contact#${branch.name.replaceAll(' ', '')}`}
                    className="text-sm capitalize text-ink-foreground/70 transition-colors hover:text-primary"
                  >
                    {branch.name}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        </div>

        <div aria-hidden className="seam seam-on-ink my-10" />

        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          <div className="flex items-center gap-3">
            <img
              className="size-12 shrink-0"
              src="/storage/ISO9001.webp"
              alt=""
              width={128}
              height={128}
              loading="lazy"
            />
            <Typography.Small className="text-ink-foreground/70">
              ISO 9001:2015 certified academy
            </Typography.Small>
          </div>

          <div className="flex items-center gap-3">
            <span
              aria-hidden
              className="inline-flex size-12 shrink-0 items-center justify-center rounded-full border border-ink-foreground/20 text-3xl font-semibold leading-none"
            >
              &#174;
            </span>
            <Typography.Small className="text-ink-foreground/70">
              Trademark registered academy
            </Typography.Small>
          </div>

          <div className="flex items-center gap-3">
            <img
              className="size-12 shrink-0"
              src="/storage/NCVTC-logo.webp"
              alt=""
              width={128}
              height={128}
              loading="lazy"
            />
            <Typography.Small className="text-ink-foreground/70">
              Affiliated to NCVTC Educational Training and Placement Council,
              Govt. of India.{' '}
              <a
                href="/storage/SAMUTHRIKA-ACADEMY-ATC-CERTIFICATE-2024-2026_NCVTC.pdf"
                className="underline decoration-1 underline-offset-4 hover:text-primary"
                download="SAMUTHRIKA-ACADEMY-ATC-CERTIFICATE-2024-2026_NCVTC.pdf"
              >
                View certificate
              </a>
            </Typography.Small>
          </div>
        </div>

        <div aria-hidden className="seam seam-on-ink my-10" />

        <div className="flex flex-col-reverse items-center justify-between gap-4 text-center md:flex-row md:text-left">
          <Typography.Small className="text-ink-foreground/50">
            © {new Date().getFullYear()} Samuthrika Academy. All rights
            reserved.
          </Typography.Small>

          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
            {legalLinks.map((link) => (
              <Link
                key={link.title}
                href={route(link.path)}
                className="text-sm text-ink-foreground/70 transition-colors hover:text-primary"
              >
                {link.title}
              </Link>
            ))}
            <Typography.Small className="text-ink-foreground/50">
              Built by{' '}
              <a
                href="https://www.linkedin.com/in/praveen-saravanan-99688b128"
                target="_blank"
                rel="noopener noreferrer"
                className="underline decoration-1 underline-offset-4 hover:text-primary"
              >
                Praveen Saravanan
              </a>
            </Typography.Small>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
